import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { Money } from 'src/feature/CardDetailHero/cardDetailHeroDemoData'
import { toStatementTransaction } from 'src/page-components/Accounts/AccountStatementsTab/accountStatements.mapping'
import type {
  StatementSummary,
  StatementTransaction,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import { physicalCardManagementQueries } from 'src/queries/physicalCardManagement/physicalCardManagement.queries'

/**
 * The ledger for ONE statement, loaded when its detail modal opens.
 *
 * Line items are a separately paginated route
 * (`GET .../statements/{statementId}/items`) rather than a field on the
 * statement, so fetching them with the list would cost one request per row on
 * tab load for ledgers that are almost never opened. The modal is already
 * mounted only while open (see `StatementListRow`), which makes it the natural
 * place to pay that cost.
 *
 * A demo statement embeds its own ledger and never issues a request — passing
 * an invented statement id to the real route would only produce a
 * `404 / CARD-MGMT.25`.
 *
 * @param creditCardId - The card the statement belongs to.
 * @param statement - The statement whose ledger to read.
 * @param isDemoStatement - True when `statement` came from the demo fallback,
 *   in which case its embedded `transactions` are the answer.
 */
export const useStatementTransactions = (
  creditCardId: string | undefined,
  statement: StatementSummary,
  isDemoStatement: boolean,
): {
  transactions: StatementTransaction[]
  isLoading: boolean
  hasError: boolean
  /**
   * True when the cursor walk hit its page cap before the server ran out of
   * pages, so `transactions` is a PREFIX of the real ledger.
   *
   * Surfaced rather than dropped because `total` is the server's authoritative
   * `statementAmount`: a capped ledger therefore renders a correct total above
   * an incomplete row list, which does not add up on screen and has no other
   * explanation. Realistically unreachable (the cap is 1,000 line items), but
   * a flag nobody reads reads as one somebody handles.
   */
  isTruncated: boolean
  /** The figure for the ledger's "Total charges" row. */
  total: Money
} => {
  const query = useQuery({
    ...physicalCardManagementQueries.statementItems({
      creditCardId,
      statementId: isDemoStatement ? undefined : statement.id,
    }),
    // A spread override REPLACES the factory's `enabled`, it does not merge
    // with it (`src/queries/AGENTS.md`), so the factory's own arg-presence
    // guard has to be restated here rather than just the demo condition —
    // dropping the `statement.id` half would let a real statement with a blank
    // id through to the queryFn.
    enabled: !isDemoStatement && Boolean(creditCardId) && Boolean(statement.id),
  })

  const items = query.data?.items

  const transactions = useMemo(() => {
    if (isDemoStatement) {
      return statement.transactions ?? []
    }

    return items?.map(toStatementTransaction) ?? []
  }, [isDemoStatement, statement.transactions, items])

  return {
    transactions,
    isTruncated: !isDemoStatement && query.data?.isComplete === false,
    // `isLoading` is false for a disabled query, so a demo statement reports
    // ready immediately rather than sitting on a spinner that will never
    // resolve.
    isLoading: !isDemoStatement && query.isLoading,
    hasError: !isDemoStatement && query.isError,
    /**
     * The figure to print on the ledger's "Total charges" row.
     *
     * For a REAL statement this is the server's own `statementAmount`, never a
     * client-side sum of the rows above it. The line-item route is a separate,
     * paginated, independently-snapshotted read, so a sum is only ever as
     * complete as what was fetched — and the statement's own total is the
     * authoritative figure by definition. Summing would also let a display
     * total silently disagree with the "Statement amount" row directly above.
     *
     * A DEMO statement still sums its embedded ledger: that arithmetic is the
     * demo's self-consistency guarantee (see `buildStatement`), and its
     * fabricated `statementAmount` is derived from the same rows anyway.
     */
    total: isDemoStatement
      ? {
          amountMinor: transactions.reduce(
            (runningTotal, transaction) =>
              runningTotal + transaction.amount.amountMinor,
            0,
          ),
          currency: statement.statementAmount.currency,
        }
      : statement.statementAmount,
  }
}
